import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'

import { createApi } from '@/shared/api'
import type { Api } from '@/shared/api'
import { useLocalization } from '@/shared/i18n'
import type { TelegramSession } from '@/shared/telegram'

const ApiContext = createContext<Api | undefined>(undefined)

export function resolveBaseUrl(configured: string | undefined): string {
  return configured?.trim() || '/api/v1'
}

const BASE_URL = resolveBaseUrl(import.meta.env.VITE_API_BASE_URL)

export function ApiProvider({
  session,
  onUnauthorized,
  children,
}: {
  session: TelegramSession
  onUnauthorized: () => void
  children: ReactNode
}) {
  const { language } = useLocalization()

  const api = useMemo(
    () =>
      createApi({
        baseUrl: BASE_URL,
        initData: () => session.initDataRaw,
        language: () => language,
        onUnauthorized,
      }),
    [session, language, onUnauthorized],
  )

  return <ApiContext value={api}>{children}</ApiContext>
}

export function useApi(): Api {
  const api = useContext(ApiContext)

  if (!api) {
    throw new Error('useApi is only usable inside an ApiProvider')
  }

  return api
}
